import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Stack,
} from '@mui/material';

import { useWeaponPresentation } from '../../hooks/useWeaponPresentation';
import SideViewChart from './WeaponPresentation/SideViewChart';
import HeightTimeChart from './WeaponPresentation/HeightTimeChart';
import MetricsCard, { MetricsItem } from './MetricsCard';
import PageBreadcrumbs from '../Shared/PageBreadcrumbs';
import { EnhancedSession } from '../../types/ui/userSession';
import { ScenarioPerformance } from '../../types/models/scenarioPerformance';
import { RepetitionPerformance } from '../../types/ui/Repetition';
import { formatAccuracy, formatDuration, formatHitFactor, formatHitRatio, formatShotScore } from '../../utils/formatters';
import { useStabilityPresentation } from '../../hooks/useStabilityPresentation';
import StabilityChart from './WeaponPresentation/StabilityChart';
import SideViewLegend from './WeaponPresentation/SideViewLegend';
import SideViewTextLegend from './WeaponPresentation/SideViewTextLegend';
import HeightTimeLegend from './WeaponPresentation/HeightTimeLegend';

const DrillDetail: React.FC = () => {
    const { sessionId, scenarioId, drillName, repetitionNumber } = useParams<{
        sessionId: string;
        scenarioId: string;
        drillName: string;
        repetitionNumber: string;
    }>();
    const navigate = useNavigate();
    const location = useLocation();

    const locationState = location.state as
        | {
            session?: EnhancedSession;
            scenario?: ScenarioPerformance;
            repetition?: RepetitionPerformance;
            totalRepetitions?: number;
        }
        | undefined;

    const [session, setSession] = React.useState<EnhancedSession | undefined>(
        () => locationState?.session
    );
    const [scenario, setScenario] = React.useState<ScenarioPerformance | undefined>(
        () => locationState?.scenario
    );

    React.useEffect(() => {
        if (locationState?.session) setSession(locationState.session);
        if (locationState?.scenario) setScenario(locationState.scenario);
    }, [locationState]);

    const {
        data: wp,
        isLoading,
        error,
    } = useWeaponPresentation(
        Number(sessionId),
        Number(scenarioId),
        drillName!,
        repetitionNumber ? Number(repetitionNumber) : undefined
    );
    const {
        data: stability,
        isLoading: isStabilityLoading,
        error: stabilityError,
    } = useStabilityPresentation(
        Number(sessionId),
        Number(scenarioId),
        drillName!,
        repetitionNumber ? Number(repetitionNumber) : undefined
    );
    const repetition = locationState?.repetition;

    const metricItems: MetricsItem[] = repetition
        ? [
            {
                label: 'Hit Score',
                value: formatShotScore(repetition.shotScore),
                icon: (
                    <img
                        src="/shootingData/accuracy.svg"
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                        alt="Accuracy Icon"
                        width={24}
                        height={24}
                        style={{
                            display: 'block',
                            filter:
                                'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
                        }}
                    />
                ),
            },
            {
                label: 'Hit Ratio',
                value: formatHitRatio(repetition.hits, repetition.hitRatio),
                icon: (
                    <img
                        src="/shootingData/ratio.svg"
                        alt="Hit Ratio"
                        width={24}
                        height={24}
                        style={{
                            display: 'block',
                            filter:
                                'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
                        }}
                    />
                ),
            },
            {
                label: 'Hit Factor',
                value: formatHitFactor(repetition.hitFactor),
                icon: (
                    <img
                        src="/shootingData/factor.svg"
                        alt="Hit Factor"
                        width={24}
                        height={24}
                        style={{
                            display: 'block',
                            filter:
                                'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
                        }}
                    />
                ),
            },
            {
                label: 'Accuracy',
                value: formatAccuracy(repetition.accuracyScore),
                icon: (
                    <img
                        src="/shootingData/score.svg"
                        alt="Accuracy"
                        width={24}
                        height={24}
                        style={{
                            display: 'block',
                            filter:
                                'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
                        }}
                    />
                ),
            },
            {
                label: 'Duration',
                value: formatDuration(repetition.speed),
                icon: (
                    <img
                        src="/shootingData/time.svg"
                        alt="Duration"
                        width={24}
                        height={24}
                        style={{
                            display: 'block',
                            filter:
                                'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
                        }}
                    />
                ),
            },
        ]
        : [];

    const repNum = locationState?.repetition?.repetitionNumber;
    const totalReps = locationState?.totalRepetitions;

    const formattedRepNum = repNum != null ? (repNum + 1).toString().padStart(2, '0') : undefined;

    const formattedTotal = totalReps?.toString().padStart(2, '0');

    if (!session || !scenario) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error">
                    Drill context is missing. Please navigate through the app.
                </Typography>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error || !wp) {
        return (
            <Box sx={{ p: 4, backgroundColor: '#0D1117', minHeight: '100vh' }}>
                <Alert severity="error">{error?.message || 'Failed to load data.'}</Alert>
            </Box>
        );
    }
    const timeSlackTime =
        wp.eventsInfo?.firstShotStart && wp.metrics?.potentialSavedTime
            ? wp.eventsInfo.firstShotStart - wp.metrics.potentialSavedTime
            : undefined;

    const eventMarkers = wp.eventsInfo
        ? [
            { time: wp.eventsInfo.buzzerStart, event: 'buzzer_start' },
            { time: wp.eventsInfo.firstShotStart, event: 'first_shot_start' },
            { time: wp.eventsInfo.presentationStartTime, event: 'presentation_start_time' },
            { time: wp.eventsInfo.presentationEndTime, event: 'presentation_end_time' },
            ...(timeSlackTime !== undefined ? [{ time: timeSlackTime, event: 'time_slack' }] : []),
        ]
        : undefined;

    return (
        <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
            <PageBreadcrumbs
                items={[
                    {
                        label: `${session.dateLabel}`,
                        to: {
                            pathname: `/client-admin/shooting-data/${session.id}`,
                            state: { session },
                        },
                    },
                    {
                        label: scenario.scenario,
                        to: {
                            pathname: `/client-admin/shooting-data/${session.id}/scenario/${scenario.userTransactionId}/repetitions`,
                            state: { session, scenario }
                        }
                    },

                    {
                        label: `Rep ${formattedRepNum}${formattedTotal ? `/${formattedTotal}` : ''}`,
                    },
                ]}
            />
            <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" sx={{ color: '#FFF', ml: 1 }}>
                        Rep {formattedRepNum} Summary
                    </Typography>
                </Box>

                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={5}>
                    <Stack
                        spacing={4}
                        alignItems="center"
                        sx={{ width: '100%', maxWidth: 1200, mx: 'auto', mt: 4 }}
                    >
                        <Box sx={{ maxWidth: '90%', mx: 'auto', mb: 4, minWidth: '1200px' }}>
                            <MetricsCard items={metricItems} />
                        </Box>

                        {/* Stability */}
                        <Box textAlign="center" width="100%">
                            <Typography color="#fff" variant="h6" mb={1}>
                                Hand Stability Over Time
                            </Typography>
                            <Box
                                sx={{
                                    boxShadow: "0 0 0 1px rgba(255,255,255,0.05)",
                                    borderRadius: 2,
                                    p: 2,
                                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                                }}
                            >
                                {isStabilityLoading && <CircularProgress />}

                                {stabilityError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {stabilityError.message || "Failed to load stability data."}
                                    </Alert>
                                )}

                                {!isStabilityLoading && !stability?.stability_plot?.length && (
                                    <Typography color="gray">No stability data available</Typography>
                                )}

                                {stability?.stability_plot?.length && (
                                    <StabilityChart
                                        data={{
                                            stability_plot: stability.stability_plot,
                                            repetition_events: stability.repetition_events ?? [],
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>







                        <Box textAlign="center" width="100%">
                            <Typography color="#fff" variant="h6" mb={1}>
                                Side View Projection
                            </Typography>

                            {/* Outer Box for background and layout */}
                            <Box
                                sx={{
                                    p: 3,
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                    borderRadius: 2,
                                    boxShadow: '0 0 0 1px rgba(255,255,255,0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 3,
                                }}
                            >
                                {!wp.sideView?.length ? (
                                    <Typography color="gray">No side view data available</Typography>
                                ) : (
                                    <>
                                        {/* Wrapper to align top row and bottom legend together */}
                                        <Box
                                            sx={{
                                                width: '100%',
                                                maxWidth: 500,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0,
                                            }}
                                        >
                                            {/* Row: Chart + Text Legend */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', md: 'row' },
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'center',
                                                    gap: 20,
                                                    width: '100%',
                                                }}
                                            >
                                                {/* Chart */}
                                                <Box width={320}>
                                                    <SideViewChart data={wp} />
                                                </Box>

                                                {/* Text Legend */}
                                                <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                                                    <SideViewTextLegend />
                                                </Box>
                                            </Box>

                                            {/* Bottom: SideViewLegend centered under both */}
                                            <Box width="100%" display="flex" justifyContent="center">
                                                <SideViewLegend metrics={wp.metrics || null} />
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                            {/* Height Time */}
                            <Box textAlign="center" width="100%">
                                <Typography color="#fff" variant="h6" mb={2}>
                                    Controller Height vs Time with Event Markers
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        backgroundColor: 'rgba(255,255,255,0.02)',
                                        boxShadow: '0 0 0 1px rgba(255,255,255,0.05)',
                                        borderRadius: 2,
                                        p: 2,
                                        minHeight: 340, // match chart + legend height
                                        gap: 2,
                                    }}
                                >
                                    {/* Chart Column */}
                                    <Box sx={{ flex: 1, position: 'relative' }}>
                                        {!wp.heightTime?.length ? (
                                            <Typography color="gray">No height-time data available</Typography>
                                        ) : (
                                            <HeightTimeChart
                                                series={wp.heightTime}
                                                eventMarkers={eventMarkers}
                                                drillMetrics={{
                                                    potential_saved_time: wp.metrics?.potentialSavedTime,
                                                    motion_time: wp.metrics?.motionTime,
                                                    reaction_time: wp.metrics?.reactionTime,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Legend Column */}
                                    <Box sx={{ width: 200, marginTop: 5 }}>
                                        <HeightTimeLegend />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>



                    </Stack>
                </Box>
            </Box>
        </Box>
    );

};

export default DrillDetail;
